import { Button, DatePicker, IconButton, Input } from '@fullscreendigital/carrefour-dex-fe'
import { Box, Checkbox, FormControlLabel, FormGroup, FormHelperText, Grid } from '@mui/material'
import { useNotification } from 'hooks'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { getInitialValues, getValidationSchema } from './helpers'
import { makeCorePutRequest } from 'utils/api'
import { useMutation } from 'react-query'
import { handleQueryError } from 'utils/helpers'
import { useFormik } from 'formik'
import { Modal } from 'components/core-ui/Modal'
import dayjs from 'dayjs'
import { AsyncAutocomplete } from 'components/core-ui/AsyncAutocomplete'
import {
  useCountriesQuery,
  useStatusAssignmentQuery,
  useStoresQuery
} from 'components/iam/partners/api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { AddShoppingCart } from '@mui/icons-material'
import { useManagersFormOptions } from '../../../hooks'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import { toAssignmentPutPayload } from './serializers'
import { NewStoreModal } from 'components/iam/partners/Stores/NewStoreModal'

const EditAssignmentDetailsModal = props => {
  const { isModalOpen, onModalClose, assignment, refetchAssignment, setShouldRefreshData } = props

  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false)
  const [storesOptions, setStoresOptions] = useState([])
  const [countriesOptions, setCountriesOptions] = useState([])

  const { showNotification } = useNotification()

  const openStoreModal = () => setIsStoreModalOpen(true)
  const closeStoreModal = () => setIsStoreModalOpen(false)

  const initialValues = useMemo(() => getInitialValues(assignment), [assignment])

  const validationSchema = useMemo(() => getValidationSchema(assignment), [assignment])

  const mutatePerson = async values => {
    await makeCorePutRequest(`/iam/external-partners/assignments/${assignment.id}`, {
      ...values,
      email: assignment.email
    })
  }

  const { isLoading, mutate } = useMutation(mutatePerson, {
    onSuccess: res => {
      refetchAssignment && refetchAssignment()
      setShouldRefreshData && setShouldRefreshData(true)
      closeModal()
      formik.resetForm()
      showNotification({
        message: 'Assignment details updated successfully.',
        type: 'success'
      })
    },
    onError: error =>
      handleQueryError(error, 'Failed to update assignment details.', showNotification)
  })

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async values => {
      mutate(toAssignmentPutPayload(values, assignment))
    }
  })

  const closeModal = () => {
    onModalClose()
    formik.resetForm()
  }

  const {
    data: storesData,
    isLoading: isStoresLoading,
    refetch: refetchStores
  } = useStoresQuery(
    showNotification,
    toTableGETUrl({
      url: '/iam/external-partners/summaries/stores',
      tableFilters: [{ name: 'isEnabled', value: true }],
      ...(formik.values.legalEntityId
        ? {
            extraCustomUrlParams: [
              {
                name: 'legalEntityId',
                value: formik.values.legalEntityId
              }
            ]
          }
        : {})
    }),
    {
      enabled:
        isModalOpen &&
        (assignment?.externalPartnerType?.name === 'Mandatar' ||
          assignment?.externalPartnerType?.name === 'Mandatar_old')
    }
  )

  useEffect(() => {
    if (storesData) {
      setStoresOptions(
        storesData.map(store => ({
          label: store.storeName ?? '',
          value: store.id
        }))
      )
    }
  }, [storesData])

  const {
    data: countriesData,
    isLoading: isCountriesLoading,
    refetch: refetchCountries
  } = useCountriesQuery(
    showNotification,
    toTableGETUrl({
      page: 0,
      rowsPerPage: 1000,
      url: '/iam/external-partners/countries'
    }),
    { enabled: isModalOpen }
  )

  useEffect(() => {
    if (countriesData) {
      setCountriesOptions(
        countriesData.map(country => ({
          label: country.name,
          value: country.id
        }))
      )
    }
  }, [countriesData])

  const { data: statusData, isLoading: statusLoading } = useStatusAssignmentQuery(
    showNotification,
    '/iam/external-partners/statuses',
    { enabled: isModalOpen }
  )

  const { statusOptions } = useManagersFormOptions(formik, statusData)

  const readOnlyStatuses = ['Terminated', 'Suspended', 'Retired']
  const isReadOnlyStatus = readOnlyStatuses.includes(formik.values?.status?.label)
  const hasChangedStatusOrEndDate =
    (formik.values.endDate && formik.values.endDate !== assignment?.endDate) ||
    formik.values?.status?.value !== assignment?.status?.id

  return (
    <>
      <NewStoreModal
        isModalOpen={isStoreModalOpen}
        closeModal={closeStoreModal}
        onSuccess={() => refetchStores()}
        selectStore={store =>
          formik.setFieldValue('store', [
            ...formik.values.store,
            {
              label: store.name,
              value: store.id
            }
          ])
        }
      />
      <Modal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        modalTitle="Edit Assignment Details"
        modalWidth="50%"
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Input
              type="text"
              variant="outlined"
              name="legalEntityName"
              label="Legal entity name (ANAF registered name for Romanian companies)"
              width="100%"
              value={formik.values.legalEntityName}
              onChange={formik.handleChange}
              required
              disabled
              error={!!formik.errors.legalEntityName}
              helperText={formik.errors.legalEntityName as string}
            />
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={5}>
                <Input
                  type="text"
                  variant="outlined"
                  name="legalEntityCIF"
                  label="Fiscal identification code (CIF)"
                  width="100%"
                  value={formik.values.legalEntityCIF}
                  onChange={formik.handleChange}
                  required
                  disabled
                  error={!!formik.errors.legalEntityCIF}
                  helperText={formik.errors.legalEntityCIF as string}
                />
              </Grid>
              <Grid item xs={7}>
                <AsyncAutocomplete
                  variant="outlined"
                  name="legalEntityCountry"
                  label="Legal entity country"
                  value={formik.values.legalEntityCountry}
                  options={countriesOptions}
                  onChange={value => formik.setFieldValue('legalEntityCountry', value)}
                  required
                  disabled
                  error={!!formik.errors.legalEntityCountry}
                  helperText={
                    typeof formik.errors.legalEntityCountry === 'object' &&
                    !Array.isArray(formik.errors.legalEntityCountry)
                      ? (formik.errors.legalEntityCountry?.value as string)
                      : ''
                  }
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Input
              type="text"
              variant="outlined"
              name="jobTitle"
              label="Job Title"
              width="100%"
              value={formik.values.jobTitle}
              onChange={formik.handleChange}
              required
              disabled={isReadOnlyStatus}
              error={!!formik.errors.jobTitle}
              helperText={formik.errors.jobTitle as string}
            />
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <DatePicker
                  variant="outlined"
                  label="Start Date"
                  value={dayjs(formik.values.startDate)}
                  onChange={value => formik.setFieldValue('startDate', value)}
                  error={!!formik.errors.startDate}
                  helperText={formik.errors.startDate as string}
                  required
                  disabled
                  style={{ width: '100%' }}
                />
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  variant="outlined"
                  label="End Date"
                  value={dayjs(formik.values.endDate)}
                  onChange={value => {
                    formik.setFieldValue('endDate', value)
                    formik.setFieldValue('updateComment', '')
                  }}
                  error={!!formik.errors.endDate}
                  helperText={formik.errors.endDate as string}
                  minDate={dayjs(formik.values.startDate)}
                  disabled={isReadOnlyStatus}
                  style={{ width: '100%' }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.adAccount}
                    name="adAccount"
                    onChange={formik.handleChange}
                  />
                }
                label="AD Account"
                disabled={isReadOnlyStatus}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.ldapAccount}
                    name="ldapAccount"
                    onChange={formik.handleChange}
                  />
                }
                label="LDAP Account"
                disabled={formik.values.googleAccount || isReadOnlyStatus} // Disable if Google Account is checked
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.googleAccount}
                    name="googleAccount"
                    onChange={event => {
                      const isChecked = (event.target as HTMLInputElement).checked
                      formik.setFieldValue('googleAccount', isChecked)
                      formik.setFieldValue('ldapAccount', isChecked)
                    }}
                  />
                }
                label="Google Account"
                disabled={isReadOnlyStatus}
              />
            </FormGroup>
            {formik.touched.googleAccount && formik.errors.googleAccount ? (
              <FormHelperText error>{formik.errors.googleAccount as string}</FormHelperText>
            ) : null}
          </Grid>
          {assignment &&
            (assignment.externalPartnerType.name === 'Mandatar' ||
              assignment.externalPartnerType.name === 'Mandatar_old') && (
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={8}>
                    <AsyncAutocomplete
                      variant="outlined"
                      name="store"
                      label="Store"
                      value={formik.values.store}
                      options={storesOptions}
                      onChange={value => formik.setFieldValue('store', value)}
                      required
                      multiple
                      disabled={
                        formik.values.status.label === 'Terminated' ||
                        formik.values.status.label === 'Retired'
                      }
                      error={!!formik.errors.store}
                      helperText={(formik.errors.store as string) || ''}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Box style={{ marginTop: '20px', textAlign: 'right' }}>
                      <IconButton
                        endIcon={<AddShoppingCart />}
                        variant="outlined"
                        label="Assign new store"
                        onClick={() => openStoreModal()}
                        style={{ flex: 'none' }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            )}
          {(hasChangedStatusOrEndDate || !!formik.values.updateComment) && (
            <Grid item xs={12}>
              <Input
                type="textarea"
                name="updateComment"
                label="Comments"
                style={{ width: '100%' }}
                rows={3}
                variant="outlined"
                value={formik.values.updateComment}
                onChange={event => formik.setFieldValue('updateComment', event.currentTarget.value)}
                disabled={!hasChangedStatusOrEndDate}
                error={!!formik.errors.updateComment}
                helperText={formik.errors.updateComment as string}
                placeholder={
                  'Please enter additional informations\nEx: Account closed - ServiceNow request number\nISIM notification - account extension period denied'
                }
                required
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <AsyncAutocomplete
              disableClearable
              variant="outlined"
              name="status"
              label="Assignment Status"
              value={formik.values.status}
              options={statusOptions}
              onChange={value => {
                const prevStatusLabel = formik.values.status?.label
                const newStatusLabel = value?.label

                const isEnded = (label?: string) =>
                  ['Terminated', 'Suspended', 'Retired'].includes(label || '')

                formik.setFieldValue('status', value)

                if (newStatusLabel === 'Terminated' || newStatusLabel === 'Retired') {
                  formik.setFieldValue('store', [])
                }

                if (
                  (isEnded(prevStatusLabel) && newStatusLabel === 'Active') ||
                  (prevStatusLabel === 'Active' && isEnded(newStatusLabel))
                ) {
                  formik.setFieldValue('updateComment', '')
                }
              }}
              required
              error={!!formik.errors.status}
              helperText={
                typeof formik.errors.status === 'object' && !Array.isArray(formik.errors.status)
                  ? (formik.errors.status?.value as string)
                  : ''
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              type="textarea"
              name="details"
              label="Assignment details"
              style={{ width: '100%' }}
              rows={3}
              variant="outlined"
              value={formik.values.details}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                formik.setFieldValue('details', event.currentTarget.value)
              }
              disabled={isReadOnlyStatus}
              error={!!formik.errors.details}
              helperText={formik.errors.details as string}
            />
          </Grid>
          <Grid item xs={12} className={s.modalButtons}>
            <Button variant="outlined" label="Cancel" onClick={closeModal} />
            <Button
              variant="contained"
              label="Save"
              onClick={() => formik.handleSubmit()}
              disabled={isLoading}
            />
          </Grid>
        </Grid>
      </Modal>
    </>
  )
}

export default EditAssignmentDetailsModal
